'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── Thesis Service ───────────────────────────────────────────────────────────

export const thesisService = {
  // Fetch all published theses (public portal)
  async getPublished() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .select(`*, thesis_keywords(keyword_code, keyword_label, keyword_area, is_student_keyword)`)
        .eq('thesis_status', 'published')
        .order('date_publication', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch published theses error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Fetch all theses for admin (all statuses)
  async getAll() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .select(`*, thesis_keywords(keyword_code, keyword_label, keyword_area, is_student_keyword), student:user_profiles!theses_student_id_fkey(full_name, email, department)`)
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch all theses error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Fetch pending theses for admin review
  async getPending() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .select(`*, thesis_keywords(keyword_code, keyword_label, keyword_area, is_student_keyword), student:user_profiles!theses_student_id_fkey(full_name, email, department)`)
        .in('thesis_status', ['submitted', 'under_review'])
        .order('created_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch pending theses error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Fetch approved/published theses for librarian reports
  async getApprovedAndPublished() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .select(`*, thesis_keywords(keyword_code, keyword_label, keyword_area, is_student_keyword), student:user_profiles!theses_student_id_fkey(full_name, email, department)`)
        .in('thesis_status', ['approved', 'published'])
        .order('approved_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch approved theses error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Fetch student's own submission
  async getMySubmission(studentId: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .select(`*, thesis_keywords(keyword_code, keyword_label, keyword_area, is_student_keyword)`)
        .eq('student_id', studentId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch my submission error:', error.message);
        return null;
      }
      return data;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Submit a new thesis (includes student_id_number and student_email)
  async submit(thesisData: any, keywords: any[], pdfEnFile: File | null, pdfElFile: File | null, studentId: string) {
    const supabase = createClient();

    try {
      // Upload PDFs to storage
      let pdfEnPath = '';
      let pdfElPath = '';

      if (pdfEnFile) {
        const fileName = `${studentId}/en_${Date.now()}_${pdfEnFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('thesis-pdfs')
          .upload(fileName, pdfEnFile, { upsert: true });
        if (uploadError) {
          console.log('PDF EN upload error:', uploadError.message);
        } else {
          pdfEnPath = uploadData?.path || '';
        }
      }

      if (pdfElFile) {
        const fileName = `${studentId}/el_${Date.now()}_${pdfElFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('thesis-pdfs')
          .upload(fileName, pdfElFile, { upsert: true });
        if (uploadError) {
          console.log('PDF EL upload error:', uploadError.message);
        } else {
          pdfElPath = uploadData?.path || '';
        }
      }

      // Insert thesis record with student_id_number and student_email
      const { data: thesis, error: thesisError } = await supabase
        .from('theses')
        .insert({
          student_id: studentId,
          student_id_number: thesisData.studentIdNumber || '',
          student_email: thesisData.studentEmail || '',
          passport_or_id_number: thesisData.passportOrIdNumber || '',
          student_country: thesisData.studentCountry || '',
          thesis_language: thesisData.thesisLanguage || '',
          doc_type: thesisData.docType,
          thesis_status: 'submitted',
          thesis_completion_status: thesisData.thesisStatus,
          access_rights: thesisData.accessRights,
          license: thesisData.license,
          embargo_date: thesisData.embargoDate || '',
          primary_lang: thesisData.langCode === 'English' ? 'en' : 'el',
          title_primary: thesisData.titlePrimary,
          abstract_primary: thesisData.abstractPrimary,
          title_secondary: thesisData.titleSecondary || '',
          abstract_secondary: thesisData.abstractSecondary || '',
          title_tertiary: thesisData.titleTertiary || '',
          abstract_tertiary: thesisData.abstractTertiary || '',
          author_surname: thesisData.authorSurname,
          author_name: thesisData.authorName,
          author_patronymic: thesisData.authorPatronymic || '',
          orcid: thesisData.orcid || '',
          supervisor_surname: thesisData.supervisorSurname,
          supervisor_name: thesisData.supervisorName,
          supervisor_patronymic: thesisData.supervisorPatronymic || '',
          committee_member_1: thesisData.committee1 || '',
          committee_member_2: thesisData.committee2 || '',
          committee_member_3: thesisData.committee3 || '',
          committee_orcid_1: thesisData.committeeOrcid1 || '',
          committee_orcid_2: thesisData.committeeOrcid2 || '',
          committee_orcid_3: thesisData.committeeOrcid3 || '',
          school: thesisData.school,
          department: thesisData.department,
          institution: thesisData.institution || 'Philips University',
          country: thesisData.country || 'Cyprus',
          language_iso: thesisData.languageIso || '',
          dc_type: thesisData.dcType || '',
          dc_uhtype: thesisData.dcUhtype || '',
          pid: thesisData.pid || '',
          scopus_categories: thesisData.scopusCategories || '',
          date_submission: thesisData.dateSubmission || null,
          date_publication: thesisData.datePublication || null,
          start_date: thesisData.startDate || '',
          expected_completion: thesisData.expectedCompletion || '',
          extent: thesisData.extent || '',
          collaborating_institution: thesisData.collaboratingInstitution || '',
          funding_source: thesisData.fundingSource || '',
          form_variant: thesisData.formVariant || 'default',
          pdf_en_path: pdfEnPath,
          pdf_el_path: pdfElPath,
        })
        .select()
        .single();

      if (thesisError) {
        if (isSchemaError(thesisError)) throw thesisError;
        console.log('Thesis insert error:', thesisError.message);
        return null;
      }

      // Insert keywords — flag student-provided (uncontrolled) entries separately
      // so librarians can tell them apart from SCOPUS categories in the UI/exports.
      if (keywords.length > 0 && thesis) {
        const kwRows = keywords.map((kw) => ({
          thesis_id: thesis.id,
          keyword_code: kw.code,
          keyword_label: kw.label,
          keyword_area: kw.area || '',
          is_student_keyword: kw.area === 'Uncontrolled' || String(kw.code || '').startsWith('UNC-'),
        }));
        const { error: kwError } = await supabase.from('thesis_keywords').insert(kwRows);
        if (kwError) console.log('Keywords insert error:', kwError.message);
      }

      return thesis;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Admin: approve thesis
  async approve(thesisId: string, adminId: string, updatedKeywords?: string[]) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('theses')
        .update({
          thesis_status: 'published',
          approved_by: adminId,
          approved_at: new Date().toISOString(),
        })
        .eq('id', thesisId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Approve thesis error:', error.message);
        return null;
      }

      // Update keywords if admin edited them
      if (updatedKeywords && updatedKeywords.length > 0) {
        await supabase.from('thesis_keywords').delete().eq('thesis_id', thesisId);
        const kwRows = updatedKeywords.map((label) => ({
          thesis_id: thesisId,
          keyword_code: label.toUpperCase().replace(/\s+/g, '-'),
          keyword_label: label,
          keyword_area: '',
          is_student_keyword: false,
        }));
        await supabase.from('thesis_keywords').insert(kwRows);
      }

      return data;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Admin: update keywords on an already-approved/published thesis
  async updateThesisKeywords(thesisId: string, keywordLabels: string[]) {
    const supabase = createClient();
    try {
      await supabase.from('thesis_keywords').delete().eq('thesis_id', thesisId);
      if (keywordLabels.length > 0) {
        const kwRows = keywordLabels.map((label) => ({
          thesis_id: thesisId,
          keyword_code: label.toUpperCase().replace(/\s+/g, '-'),
          keyword_label: label,
          keyword_area: '',
          is_student_keyword: false,
        }));
        const { error } = await supabase.from('thesis_keywords').insert(kwRows);
        if (error) {
          console.log('Update keywords error:', error.message);
          return false;
        }
      }
      return true;
    } catch (err: any) {
      console.log('Update keywords error:', err.message);
      return false;
    }
  },

  // Admin: send back thesis with comment + trigger email notification
  async sendBack(thesisId: string, comment: string) {
    const supabase = createClient();
    try {
      // First get the thesis to find student emails
      const { data: thesis } = await supabase
        .from('theses')
        .select(`*, student:user_profiles!theses_student_id_fkey(full_name, email)`)
        .eq('id', thesisId)
        .single();

      // Update thesis status
      const { data, error } = await supabase
        .from('theses')
        .update({
          thesis_status: 'sent_back',
          sent_back_comment: comment,
          sent_back_at: new Date().toISOString(),
        })
        .eq('id', thesisId)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Send back thesis error:', error.message);
        return null;
      }

      // Send email notifications to both registered email and student_email
      if (thesis) {
        const registeredEmail = thesis.student?.email || '';
        const studentEmail = thesis.student_email || '';
        const studentName = thesis.student?.full_name || `${thesis.author_name} ${thesis.author_surname}`;
        const thesisTitle = thesis.title_primary || 'Untitled Thesis';

        const emails = new Set<string>();
        if (registeredEmail) emails.add(registeredEmail);
        if (studentEmail) emails.add(studentEmail);

        // Send notification for each unique email
        for (const email of emails) {
          try {
            await fetch('/api/send-notification', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                recipientEmail: email,
                studentName,
                thesisTitle,
                comment,
                thesisId,
                notificationType: 'sent_back',
              }),
            });
          } catch (notifyErr) {
            console.log('Email notification error:', notifyErr);
          }
        }
      }

      return data;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Admin: delete thesis
  async delete(thesisId: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('theses').delete().eq('id', thesisId);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete thesis error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Mark theses as exported
  async markExported(thesisIds: string[], adminId: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase.rpc('mark_theses_exported', {
        thesis_ids: thesisIds,
        admin_id: adminId,
      });
      if (error) {
        console.log('Mark exported error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Mark exported error:', err.message);
      return false;
    }
  },

  // Unmark theses as exported
  async unmarkExported(thesisIds: string[]) {
    const supabase = createClient();
    try {
      const { error } = await supabase.rpc('unmark_theses_exported', {
        thesis_ids: thesisIds,
      });
      if (error) {
        console.log('Unmark exported error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Unmark exported error:', err.message);
      return false;
    }
  },

  // Get PDF signed URL
  async getPdfUrl(path: string) {
    if (!path) return null;
    const supabase = createClient();
    const { data, error } = await supabase.storage
      .from('thesis-pdfs')
      .createSignedUrl(path, 3600);
    if (error) {
      console.log('Get PDF URL error:', error.message);
      return null;
    }
    return data?.signedUrl || null;
  },

  // Analytics
  async getAnalytics() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.rpc('get_thesis_analytics');
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Analytics error:', error.message);
        return null;
      }
      return data?.[0] || null;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  // Report analytics
  async getReportAnalytics() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase.rpc('get_report_analytics');
      if (error) {
        console.log('Report analytics error:', error.message);
        return null;
      }
      return data?.[0] || null;
    } catch (err: any) {
      console.log('Report analytics error:', err.message);
      return null;
    }
  },
};

// ─── Elsevier Keywords Service ────────────────────────────────────────────────

export const keywordService = {
  async getAll() {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('elsevier_keywords')
        .select('*')
        .eq('is_active', true)
        .order('area', { ascending: true })
        .order('label', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Fetch keywords error:', error.message);
        return [];
      }
      return data || [];
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async create(code: string, label: string, area: string) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('elsevier_keywords')
        .insert({ code, label, area })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Create keyword error:', error.message);
        return null;
      }
      return data;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async update(id: string, updates: { label?: string; area?: string; is_active?: boolean }) {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('elsevier_keywords')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Update keyword error:', error.message);
        return null;
      }
      return data;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },

  async delete(id: string) {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('elsevier_keywords').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.log('Delete keyword error:', error.message);
        return false;
      }
      return true;
    } catch (err: any) {
      console.log('Schema error:', err.message);
      throw err;
    }
  },
};

// ─── User Admin Service ────────────────────────────────────────────────────────
// Admin-only: manage roles (librarian/admin) and pre-authorize emails.

export const userAdminService = {
  async listUsers() {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, full_name, role, department, institution, created_at')
      .order('created_at', { ascending: false });
    if (error) {
      console.log('List users error:', error.message);
      return [];
    }
    return data || [];
  },

  async setUserRole(userId: string, role: 'student' | 'librarian' | 'admin') {
    const supabase = createClient();
    // Prefer the RPC (runs with SECURITY DEFINER and checks is_admin_user).
    const { error: rpcError } = await supabase.rpc('admin_set_user_role', {
      p_user_id: userId,
      p_role: role,
    });
    if (!rpcError) return true;
    // Fallback: direct update (works if RLS policy allows).
    const { error } = await supabase
      .from('user_profiles')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('id', userId);
    if (error) {
      console.log('Set role error:', error.message);
      return false;
    }
    return true;
  },

  async listPreAuthorized() {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('pre_authorized_librarians')
      .select('*')
      .order('added_at', { ascending: false });
    if (error) {
      console.log('List pre-authorized error:', error.message);
      return [];
    }
    return data || [];
  },

  async addPreAuthorized(opts: {
    email: string;
    provider?: 'any' | 'google' | 'azure';
    role?: 'librarian' | 'admin';
    note?: string;
    addedBy?: string;
  }) {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('pre_authorized_librarians')
      .insert({
        email: opts.email.trim().toLowerCase(),
        provider: opts.provider || 'any',
        role: opts.role || 'librarian',
        note: opts.note || '',
        added_by: opts.addedBy || null,
      })
      .select()
      .single();
    if (error) {
      console.log('Add pre-authorized error:', error.message);
      throw error;
    }
    return data;
  },

  async removePreAuthorized(id: string) {
    const supabase = createClient();
    const { error } = await supabase
      .from('pre_authorized_librarians')
      .delete()
      .eq('id', id);
    if (error) {
      console.log('Remove pre-authorized error:', error.message);
      return false;
    }
    return true;
  },
};

// ─── Draft service: save-and-resume for the wizard ──────────────────────
// Strategy: DB-first (Supabase row keyed by student_id). If the DB write
// fails (offline, schema not migrated, RLS blocks), we fall back to
// localStorage so the student never loses their progress. On load we
// prefer the DB copy when it exists and looks newer than the local one.

const DRAFT_LOCAL_KEY_PREFIX = 'thesis-draft:';

function localKey(studentId: string) {
  return `${DRAFT_LOCAL_KEY_PREFIX}${studentId}`;
}

export const draftService = {
  // Persist a draft. Always writes localStorage (instant, offline-safe),
  // and additionally tries to upsert into Supabase. Returns a struct
  // indicating which stores succeeded.
  async save(studentId: string, payload: any, currentStep: number, viewLang: 'en' | 'el' | 'both'):
    Promise<{ db: boolean; local: boolean; error?: string }> {
    const result = { db: false, local: false, error: undefined as string | undefined };

    // Local first — synchronous, can't fail in any meaningful way.
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(localKey(studentId), JSON.stringify({
          payload, currentStep, viewLang, updatedAt: new Date().toISOString(),
        }));
        result.local = true;
      } catch (e: any) {
        // Quota exceeded or storage disabled — not fatal, DB might still work.
        result.error = `Local save failed: ${e?.message || 'unknown'}`;
      }
    }

    // DB attempt.
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('thesis_drafts')
        .upsert({
          student_id: studentId,
          payload,
          current_step: currentStep,
          view_lang: viewLang,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'student_id' });
      if (error) {
        if (isSchemaError(error)) {
          // Table not present yet (migration not run). Local save still succeeded.
          result.error = 'Drafts table not migrated yet — saved locally only.';
        } else {
          result.error = error.message;
        }
      } else {
        result.db = true;
      }
    } catch (err: any) {
      result.error = err?.message || 'DB save error';
    }
    return result;
  },

  // Load a draft. Tries DB first; falls back to localStorage if the DB copy
  // is missing or older. Returns null if there's nothing anywhere.
  async load(studentId: string): Promise<null | { payload: any; currentStep: number; viewLang: 'en' | 'el' | 'both'; source: 'db' | 'local' }> {
    let dbDraft: any = null;
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('thesis_drafts')
        .select('*')
        .eq('student_id', studentId)
        .maybeSingle();
      if (!error && data) dbDraft = data;
    } catch {
      // ignore; fall back below
    }

    let localDraft: any = null;
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(localKey(studentId));
        if (raw) localDraft = JSON.parse(raw);
      } catch { /* ignore */ }
    }

    // Pick whichever is newer.
    if (dbDraft && localDraft) {
      const dbTime = new Date(dbDraft.updated_at).getTime();
      const localTime = new Date(localDraft.updatedAt).getTime();
      if (dbTime >= localTime) {
        return { payload: dbDraft.payload, currentStep: dbDraft.current_step, viewLang: dbDraft.view_lang, source: 'db' };
      }
      return { payload: localDraft.payload, currentStep: localDraft.currentStep, viewLang: localDraft.viewLang, source: 'local' };
    }
    if (dbDraft) {
      return { payload: dbDraft.payload, currentStep: dbDraft.current_step, viewLang: dbDraft.view_lang, source: 'db' };
    }
    if (localDraft) {
      return { payload: localDraft.payload, currentStep: localDraft.currentStep, viewLang: localDraft.viewLang, source: 'local' };
    }
    return null;
  },

  // Wipe the draft after a successful submit.
  async clear(studentId: string): Promise<void> {
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem(localKey(studentId)); } catch { /* ignore */ }
    }
    try {
      const supabase = createClient();
      await supabase.from('thesis_drafts').delete().eq('student_id', studentId);
    } catch { /* ignore */ }
  },
};
